import BannerDisplay from '~/components/BannerDisplay.tsx';
import MuiBox from '@mui/material/Box';
import { Carousel, CarouselProps } from 'react-responsive-carousel';
import Typography from '~/components/Typography.tsx';
import useAdvertisements from '~/hooks/useAdvertisements.ts';
import { Advertisement, AdvertisementType } from '~kup/models/Advertisement.ts';
import { Skeleton } from '@mui/material';
import { useMemo, useState } from 'react';
import useBatchLogger from '~/hooks/useBatchLogger.ts';
import { extractAdvertisementEventData } from '~/utils/advertisement.ts';

export type AdvertisementsProps = {
  type: AdvertisementType;
  category?: string[];
  excludeCategory?: string[];
  borderRadius?: string;
  fixRandom?: boolean;
  disableSkeleton?: boolean;
  hideImage?: boolean;
};

export default function Advertisements(props: AdvertisementsProps) {
  const advertisements = useAdvertisements(props.type);
  const borderRadius = props.borderRadius ?? (props.type === 'SQUARE' ? '16px' : '8px');
  const disableIndicator = props.type !== 'SQUARE';
  const [randomValue] = useState<number>(Math.random());
  const fixIndex =
    advertisements && advertisements.length > 0 && props.fixRandom
      ? Math.floor(advertisements.length * randomValue)
      : -1;
  const displayAdvertisements = useMemo(
    () =>
      advertisements && advertisements.length > 0
        ? advertisements
            .filter((_, idx) => fixIndex === -1 || fixIndex === idx)
            .sort(() => Math.random() - 0.5)
        : [],
    [randomValue, advertisements?.length]
  );

  if (advertisements === null)
    return props.disableSkeleton ? null : (
      <AdvertisementSkeleton type={props.type} borderRadius={props.borderRadius} />
    );
  if (advertisements.length === 0) return null;

  return (
    <AdvertisementsCarousel borderRadius={borderRadius} disableIndicator={disableIndicator}>
      {displayAdvertisements.map((ad) => (
        <AdvertisementDisplay key={ad.slug} advertisement={ad} hideImage={props.hideImage} />
      ))}
    </AdvertisementsCarousel>
  );
}

export function AdvertisementSkeleton(props: { type: AdvertisementType; borderRadius?: string }) {
  const height =
    props.type === 'SQUARE'
      ? 'calc((100vw - 40px) * 0.955)'
      : props.type === 'THIN'
        ? '68px'
        : '80px';
  const borderRadius = props.borderRadius ?? (props.type === 'SQUARE' ? '16px' : '8px');
  return (
    <Skeleton
      variant={'rounded'}
      width={'100%'}
      height={height}
      animation={'wave'}
      sx={{ borderRadius: borderRadius }}
    />
  );
}

export type AdvertisementProps = {
  advertisement: Advertisement;
  hideImage?: boolean;
};

export function AdvertisementDisplay(props: AdvertisementProps) {
  const advertisement = props.advertisement;
  const advertisementSlug = advertisement?.slug;
  const advertisementCategoryCode = advertisement?.categoryCode;
  const [randomValue] = useState<number>(Math.random());
  const { logAction } = useBatchLogger();
  const randomBanner =
    advertisement?.banners?.[Math.floor(advertisement.banners.length * randomValue)];

  const handleClickBanner = () => {
    if (advertisement && randomBanner) {
      logAction({
        eventType: 'CLICK',
        eventTarget: 'ADVERTISEMENT_BANNER',
        eventCategory: 'advertisement',
        eventData: extractAdvertisementEventData(advertisement, randomBanner),
      });
    }
  };

  if (!randomBanner) return null;
  return (
    <BannerDisplay
      advertisementSlug={advertisementSlug}
      advertisementCategory={advertisementCategoryCode}
      banner={randomBanner}
      hideImage={props.hideImage}
      onClick={handleClickBanner}
    />
  );
}

function AdvertisementsCarousel(props: {
  children: CarouselProps['children'] | React.ReactChild;
  borderRadius?: string;
  disableIndicator?: boolean;
}) {
  return (
    <MuiBox
      sx={{
        position: 'relative',
        borderRadius: props.borderRadius ?? '16px',
        overflow: 'hidden',
      }}
    >
      {props.children && Array.isArray(props.children) && props.children.length > 1 ? (
        <Carousel
          infiniteLoop
          swipeable
          autoPlay
          interval={5000}
          transitionTime={0}
          showArrows={false}
          showStatus={false}
          showThumbs={false}
          showIndicators={!props.disableIndicator}
          renderIndicator={(_, isSelected, index) => {
            return (
              <MuiBox
                key={index}
                sx={{
                  display: isSelected ? 'flex' : 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '24px',
                  width: '40px',
                  position: 'absolute',
                  bottom: '4px',
                  right: '12px',
                  color: 'white',
                  background: 'rgba(0, 0, 0, 0.6)',
                  px: '8px',
                  py: '2px',
                  borderRadius: '12px',
                  overFlow: 'hidden',
                }}
              >
                <Typography variant={'Caption'}>
                  {index + 1} / {(props.children as Array<React.ReactChild>).length}
                </Typography>
              </MuiBox>
            );
          }}
        >
          {props.children}
        </Carousel>
      ) : (
        props.children
      )}
    </MuiBox>
  );
}
